/**
 * Visor 3D de archivos IFC (BIM) para /planos.
 *
 * Un IFC no se convierte a SVG como el DWG/DXF: se ve en 3D directo desde el archivo, en el
 * navegador. `three` se carga por CDN de forma perezosa (no va al bundle). El modelo lo LEE un Web
 * Worker (`lib/ifc.worker.ts`: web-ifc + medición + geometría), así la pestaña no se congela mientras
 * se abre y la pantalla muestra el avance real de cada etapa.
 *
 * Además del render, el visor arma un INVENTARIO por tipo de elemento (IfcWall, IfcSlab, …) que
 * se entrega como `PlanoInventario` (usando `capas`, no `bloques`: la selección por capa del
 * plano NO depende del SVG, así reusamos tal cual el panel izquierdo, la selección y el armado
 * de la HP). Cada tipo IFC es una "capa": clic en el panel o clic en el modelo 3D la selecciona.
 *
 * FLUIDEZ (medida en GNI_model_5_arc, 77 MB: antes 8 cuadros por segundo al girar):
 *  - la geometría llega en LOTES fusionados (`lib/visorLotes`): pocas llamadas de dibujo por cuadro en
 *    vez de un objeto por pieza y otro por sus aristas. La selección sigue siendo por pieza (cada pieza
 *    es un rango del lote: su color se escribe en ese rango, ocultarla es sacarla del índice);
 *  - las aristas se apagan mientras se mueve la cámara y vuelven al soltar;
 *  - se dibuja A DEMANDA (al mover la cámara o cambiar algo), no 60 veces por segundo con todo quieto;
 *  - el encuadre y el centro de giro van sobre lo que SE CONSTRUYE (sin genéricos, mobiliario ni
 *    entorno), para que un modelo con calles o árboles no quede diminuto en una esquina.
 *
 * El archivo se descarga autenticado (cookie de sesión) de
 * `GET /api/v1/projects/:projectId/files/:fileId/download`.
 */
import type { CajaEnPantalla } from './PlanoViewerMlightcad.js';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@fluentui/react-components';
import { decisionIfc, elementosDelInventarioIfc, type PlanoInventario } from '@supernova/shared';
import type { NivelIfc, ObraIfc } from '../lib/medicionIfc.js';
import { getParametrosMedicionIfc } from '../api/calibracion.js';
import { indicesVisibles, trozoDeVertice, T_EID, T_I0, T_IN, T_PASO, T_V0, T_VN, type Lote, type Visibilidad } from '../lib/visorLotes.js';
import type { FaseDeCarga, MensajeDelWorker, PedidoAlWorker, TiposDeMaterialAlWorker } from '../lib/ifc.worker.js';
import { clasificarMaterialesIfc } from '../api/planos.js';
import { WEBIFC_URL, WEBIFC_WASM } from '../lib/webIfc.js';
import { corteDePieza, unirCajas, type CajaDePieza } from '../lib/avanceObra4d.js';
import { CROQUIS_ALTO, CROQUIS_ANCHO, reducirLienzo, type Croquis, type PedidoCroquisIfc } from '../lib/croquis.js';

// esm.sh reescribe los `import ... from 'three'` internos (p. ej. de OrbitControls) a URLs
// absolutas y comparte una sola instancia de three entre módulos, así no hace falta un importmap
// en index.html.
const THREE_URL = 'https://esm.sh/three@0.160.0';
const ORBIT_URL = 'https://esm.sh/three@0.160.0/examples/jsm/controls/OrbitControls.js';
// Sin post-proceso: el SAOPass (oclusión ambiental) ennegrece los IFC reales (probado en FZK-Haus y
// un Revit con intensidades 0.02–0.14: manchas negras, no es cuestión de calibrar). Si vuelve el AO,
// probar GTAOPass contra IFC reales, no SAOPass.
// La versión de web-ifc (y por qué esa) vive en lib/webIfc.ts: la comparte la medición sin visor
// («Cambió el plano»), que tiene que medir con el MISMO motor que el visor.

// Ángulo mínimo entre caras vecinas para dibujar una arista. A 35° quedan los quiebres reales; a 20°
// se colaban las líneas de teselado de piezas curvas (medido: −10% de segmentos en FZK-Haus, sin
// perder contornos). Presentación.
const ANGULO_ARISTA = 35;
// Tamaño de cada lote de geometría (vértices). Es un tope TÉCNICO de buffer (para que el modelo llegue
// por partes y la pantalla muestre avance), no un umbral de "modelo grande": todo modelo va por lotes.
const LOTE_VERTICES = 1 << 20;

// Colores FUNCIONALES de selección (constantes, no cambian con el tema — como el rojo del SVG 2D).
// Son overrides SÓLIDOS y saturados que ningún material del modelo usa, para que la selección/foco
// SALTE a la vista sobre el fondo oscuro y entre piezas del mismo tipo (un tinte tenue se lava).
const COLOR_FOCO = 0xff2bd6; // magenta puro: la pieza enfocada desde el panel
const COLOR_PENDIENTE = 0x2b8cff; // azul brillante: piezas del tipo activo aún sin concepto
const COLOR_FALTA = 0xe5484d; // «¿Qué me falta?»: se construye y no está en la hoja

/** Tonos de estado con los que «Cambió el plano» pinta piezas (los mismos del tema, para que sigan dark/light). */
export type TonoDePieza = 'ok' | 'bad' | 'warn' | 'accent';
// Respaldo si el tema no da un color que THREE entienda (p. ej. color-mix u oklch).
const RESPALDO_TONO: Record<TonoDePieza, number> = { ok: 0x2ea043, bad: COLOR_FALTA, warn: 0xd9a400, accent: 0x3b82f6 };

/** Los tonos de estado leídos del tema vigente (--ok, --bad, --warn, --accent); sin tema legible, el respaldo. */
function tonosDeEstado(THREE: any): Record<TonoDePieza, { r: number; g: number; b: number }> {
  let css: CSSStyleDeclaration | null = null;
  try {
    css = getComputedStyle(document.documentElement);
  } catch {
    css = null;
  }
  const out = {} as Record<TonoDePieza, { r: number; g: number; b: number }>;
  for (const k of Object.keys(RESPALDO_TONO) as TonoDePieza[]) {
    const c = new THREE.Color(RESPALDO_TONO[k]);
    const raw = css?.getPropertyValue(`--${k}`).trim() ?? '';
    // Solo formatos que THREE sabe leer: otro (oklch, color-mix) avisaría en consola; se queda el respaldo.
    if (/^(#|rgb|hsl)/i.test(raw)) c.setStyle(raw);
    out[k] = { r: c.r, g: c.g, b: c.b };
  }
  return out;
}
const COLOR_ARISTA = 0x14171b;

interface Props {
  /** Origen del IFC: (a) archivo del proyecto (projectId+fileId), o (b) una URL directa (`srcUrl`). */
  projectId?: string;
  fileId?: string;
  /**
   * URL directa del IFC (autenticada por cookie). Si se pasa, tiene prioridad sobre
   * projectId/fileId — la usa el prototipo "de la HP a la geometría", que sirve el
   * IFC generado desde `/api/v1/quotes/:id/geometria/ifc`.
   */
  srcUrl?: string;
  /** Inventario por tipo de elemento, una vez cargado el modelo (para el panel izquierdo). */
  onInventario?: (inv: PlanoInventario) => void;
  /**
   * Sin barra de estado al pie (gestos y conteo de piezas). La usa /planos cuando el visor no tiene
   * alto (preset «Solo la hoja»): la barra va pegada al pie y, con alto 0, se salía por ARRIBA del
   * visor y quedaba encima de las pestañas de planos.
   */
  sinBarraDeEstado?: boolean;
  /** Capas (tipos IFC) seleccionadas actualmente, por su etiqueta — para resaltarlas en 3D. */
  capasSel?: Set<string>;
  /** Clic sobre un elemento del modelo 3D → seleccionar su tipo (misma acción que el panel). */
  onPickCapa?: (etiqueta: string) => void;
  /**
   * Instancias EXCLUIDAS de la selección (por expressID del elemento IFC). Con un tipo activo, el
   * usuario saca instancias que no van; acá se atenúan en 3D y la cantidad se recalcula sin ellas.
   */
  instExcluidas?: Set<number>;
  /**
   * Clic sobre una instancia del TIPO ACTIVO → sacarla/reingresarla de la selección (por expressID).
   * Solo aplica cuando hay un tipo activo (si no, un clic elige el tipo con `onPickCapa`).
   */
  onToggleInstancia?: (expressID: number) => void;
  /** Clic en vacío (con un tipo activo) → soltar la selección y restaurar todos los tipos. */
  onPickVacio?: () => void;
  /**
   * Instancias que YA tienen concepto asignado en la HP (por expressID), para pintar el estado de
   * asignación: dentro del tipo activo se ven grisadas (ya hechas) y las que faltan quedan resaltadas.
   */
  instAsignadas?: Set<number>;
  /**
   * Instancia ENFOCADA desde el panel (por expressID): se realza con un acento propio para localizarla
   * en la escena sin sacarla del conteo. null = ninguna.
   */
  instEnfoque?: number | null;
  /**
   * Instancias OCULTAS por los comandos de visibilidad (ocultar/aislar/ocultar asignadas), por
   * expressID. Es solo VISTA: no cambia la selección ni la asignación. Las ocultas salen del raycast.
   */
  instOcultas?: Set<number>;
  /**
   * «¿Qué me falta?»: piezas que SE CONSTRUYEN (por expressID). Con algo aquí y sin selección, el
   * modelo se pinta entero: rojo lo que no tiene concepto, gris lo que ya está en la hoja y casi
   * transparente lo que no se presupuesta. null/ausente = colores del modelo.
   */
  instAPresupuestar?: Set<number> | null;
  /**
   * «Cambió el plano»: color de estado por pieza (expressID → tono). Con algo aquí y sin selección por tipo, lo que
   * está en el mapa se pinta sólido con su tono (los del tema: --ok, --bad, --warn, --accent) y lo demás queda casi
   * transparente, como contexto. null/ausente = colores del modelo. Precedencia: croquis > enfoque > instColores >
   * instAPresupuestar > selección (si se pasan `instColores` e `instAPresupuestar`, gana `instColores`).
   */
  instColores?: ReadonlyMap<number, TonoDePieza> | null;
  /**
   * Avance de obra (idea 11, «Avance 4D»): cuánto está construido de cada pieza (expressID → 0 a 1). Con algo aquí y
   * sin selección por tipo: lo terminado (1) con los colores del modelo, lo que se está construyendo (entre 0 y 1) se
   * recorta en su plano de crecimiento —lo hecho sólido en azul de acento, lo que falta como fantasma— y lo que aún
   * no empieza (0) queda casi transparente. Lo que no está en el mapa queda tenue, como contexto. El recorte sale de
   * las medidas de la pieza (`corteDePieza`): arriba si es más alta que su lado menor, a lo largo si es plana.
   * Precedencia: croquis > enfoque > avance4d > instColores > instAPresupuestar > selección. null/ausente = nada de
   * esto (el visor se comporta como siempre y no gasta memoria extra).
   */
  avance4d?: ReadonlyMap<number, number> | null;
  /**
   * Solo con `avance4d`: las piezas (expressID) de UN concepto que se quiere ubicar. Se marcan en el color de foco aunque aún
   * no estén construidas (las que faltan, a media opacidad) y todo lo demás se atenúa. Vacío o null = sin resalte.
   */
  avance4dResaltadas?: ReadonlySet<number> | null;
  /**
   * Al terminar de cargar el modelo: la caja (mundo, m) de cada pieza por expressID. La necesita quien reparte el
   * avance entre las piezas de un concepto en el orden en que se construyen.
   */
  onCajasDePiezas?: (cajas: ReadonlyMap<number, CajaDePieza>) => void;
  /**
   * "Ver por nivel": id del piso (IfcBuildingStorey) o de la parte de la instalación (IfcFacilityPart)
   * a mostrar; null = todo el modelo. Un piso se CORTA a `alturaCorte` sobre su piso, para leer el
   * interior como una planta; una parte (sin altura de piso) solo se filtra. Es solo VISTA.
   */
  nivelVista?: number | null;
  /** Altura del corte sobre el piso del nivel elegido, en metros (1,2 por defecto); null = sin corte. */
  alturaCorte?: number | null;
  /** Oculta el techo (IfcRoof y sus partes, losas ROOF, recubrimientos ROOFING). Solo VISTA. */
  ocultarTecho?: boolean;
  /**
   * Al terminar de cargar: los niveles con elementos (ordenados por altura), el nivel de cada elemento
   * (para el árbol Nivel → Categoría → Tipo → Pieza) y si el modelo tiene techo (para "Ocultar techo").
   */
  onNiveles?: (
    niveles: NivelIfc[],
    nivelDeElemento: Map<number, number>,
    hayTecho: boolean,
    /** Obras del modelo (IfcFacility) con piezas, y de cuál es cada pieza. */
    obras?: { obras: ObraIfc[]; obraDeElemento: Map<number, number> },
  ) => void;
  /**
   * Clic IZQUIERDO sobre una pieza: seleccionar ESA pieza (como un visor BIM). `sumar` = Ctrl/⌘ (sumar o
   * quitar). `null` = clic en vacío. Si se pasa, reemplaza el clic por tipo (`onPickCapa`/…).
   */
  onPickPieza?: (expressID: number | null, sumar: boolean) => void;
  /** Si se pasa, SOLO estas piezas se ven (aislar); el resto se oculta. Solo VISTA. */
  instSoloVisibles?: Set<number> | null;
  /**
   * Orden de cámara: cada vez que cambia `n`, encuadra la SELECCIÓN (conservando el ángulo), pone la
   * vista SUPERIOR o vuelve a TODO el modelo.
   */
  ordenCamara?: { n: number; vista: 'seleccion' | 'arriba' | 'todo' | 'resaltadas' } | null;
  /**
   * Para la miniatura de PlanosPage: el visor entrega una función que dice DÓNDE está en pantalla (px del
   * contenedor, con la cámara de ahora) lo seleccionado, o lo que se construye si no hay selección. No
   * mueve la cámara. null mientras el modelo no está listo.
   */
  onCajaEnPantalla?: (leer: ((todo?: boolean) => CajaEnPantalla | null) | null) => void;
  /**
   * Clic DERECHO sin arrastrar sobre una pieza → pedir que se oculte (como "Ocultar elemento" de
   * Revit/Navisworks), para ir pelando y llegar a lo de adentro. El padre la suma a `instOcultas`.
   */
  onOcultarPieza?: (expressID: number) => void;
  /**
   * Al terminar la PRIMERA vista (modelo dibujado y encuadrado): una imagen chica (webp, ~480 px,
   * 16:10) de lo que se ve, para la miniatura de la galería de planos. Best-effort: si el navegador
   * no puede capturar, no se llama.
   */
  onPrimeraVista?: (imagen: Blob) => void;
  /**
   * Números generadores: el visor entrega una función que toma el CROQUIS (PNG en data URL) de unas piezas
   * (por expressID) o tipos (por etiqueta) resaltados sobre el resto atenuado, encuadrados, sobre fondo
   * blanco. No cambia lo que ve la persona: al terminar, todo vuelve a como estaba.
   */
  onCapturador?: (capturar: ((pedido: PedidoCroquisIfc) => Promise<Croquis | null>) | null) => void;
}

type Estado =
  | { fase: 'cargando'; nota: string; fraccion: number | null; dibujando: boolean }
  | { fase: 'listo'; elementos: number; triangulos: number; segundos: number }
  | { fase: 'error'; msg: string };

/** Un lote en pantalla: su geometría fusionada, sus dos mallas (sólida / translúcida) y sus aristas. */
interface LoteVisor {
  lote: Lote;
  /** Etiqueta (tipo) de cada trozo; null = pieza que no está en el inventario (terreno, anotación…). */
  etiquetas: (string | null)[];
  color: any; // BufferAttribute RGBA por vértice
  geoS: any;
  geoT: any;
  geoA: any;
  /** Recorte por vértice (nx, ny, nz, corte) del avance de obra; null mientras nadie lo pida. */
  corte: any | null;
  meshS: any;
  meshT: any;
  lineas: any;
}

/** Texto del avance, para una persona (sin nombres de componentes). */
function textoDeAvance(fase: FaseDeCarga, fraccion: number | null, mb?: number): string {
  const pct = fraccion != null ? ` ${Math.round(fraccion * 100)} %` : '';
  switch (fase) {
    case 'motor':
      return 'Preparando el visor 3D…';
    case 'descarga':
      return fraccion != null ? `Descargando el modelo…${pct}` : `Descargando el modelo… ${mb != null ? `${mb.toFixed(0)} MB` : ''}`;
    case 'apertura':
      return `Abriendo el modelo${mb != null ? ` (${mb.toFixed(1)} MB)` : ''}…`;
    case 'inventario':
      return `Leyendo el modelo…${pct}`;
    case 'geometria':
      return `Dibujando el modelo…${pct}`;
    case 'cierre':
      return 'Midiendo las piezas…';
  }
}

/**
 * Encuadre exacto: pone la cámara en la dirección `desde` (relativa al centro) a la distancia mínima
 * para que las 8 esquinas de la caja entren en el cuadro, más un margen. Devuelve el centro y el radio
 * de la caja (para el centro de giro y near/far).
 */
function encuadrar(THREE: any, camera: any, box: any, desde: [number, number, number], margen: number) {
  const ctr = box.getCenter(new THREE.Vector3());
  const f = new THREE.Vector3(...desde).normalize().negate(); // de la cámara hacia el centro
  const right = new THREE.Vector3().crossVectors(f, new THREE.Vector3(0, 1, 0)).normalize();
  const up = new THREE.Vector3().crossVectors(right, f).normalize();
  const tanV = Math.tan((camera.fov * Math.PI) / 360);
  const tanH = tanV * camera.aspect;
  let d = 0;
  for (const x of [box.min.x, box.max.x])
    for (const y of [box.min.y, box.max.y])
      for (const z of [box.min.z, box.max.z]) {
        const rel = new THREE.Vector3(x, y, z).sub(ctr);
        const dz = rel.dot(f);
        d = Math.max(d, Math.abs(rel.dot(right)) / tanH - dz, Math.abs(rel.dot(up)) / tanV - dz);
      }
  camera.position.copy(ctr).addScaledVector(f, -d * margen);
  return { ctr, radio: box.getSize(new THREE.Vector3()).length() / 2 || 10 };
}

export function IfcViewer3D({
  projectId,
  fileId,
  srcUrl,
  sinBarraDeEstado,
  onInventario,
  capasSel,
  onPickCapa,
  instExcluidas,
  onToggleInstancia,
  onPickVacio,
  instAsignadas,
  instEnfoque,
  instOcultas,
  instAPresupuestar,
  instColores,
  avance4d = null,
  avance4dResaltadas = null,
  onCajasDePiezas,
  nivelVista = null,
  alturaCorte = 1.2,
  ocultarTecho = false,
  onNiveles,
  onOcultarPieza,
  onPickPieza,
  instSoloVisibles = null,
  ordenCamara = null,
  onPrimeraVista,
  onCajaEnPantalla,
  onCapturador,
}: Props) {
  const onPrimeraVistaRef = useRef<Props['onPrimeraVista']>(onPrimeraVista);
  onPrimeraVistaRef.current = onPrimeraVista;
  const onCajasDePiezasRef = useRef<Props['onCajasDePiezas']>(onCajasDePiezas);
  onCajasDePiezasRef.current = onCajasDePiezas;
  // Uniforme compartido por los dos materiales: 1 = el recorte por pieza del avance de obra está activo.
  const avanceActivoRef = useRef<{ value: number }>({ value: 0 });
  // Caja por pieza (expressID), unida entre sus trozos; se rehace si llegan lotes nuevos.
  const cajasPiezaRef = useRef<{ trozos: number; mapa: Map<number, CajaDePieza> }>({ trozos: -1, mapa: new Map() });
  // Origen del modelo: URL directa (prototipo geometría) o descarga del archivo del proyecto.
  const downloadUrl = srcUrl ?? `/api/v1/projects/${projectId}/files/${fileId}/download`;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando', nota: 'Preparando el visor 3D…', fraccion: null, dibujando: false });
  const [aviso, setAviso] = useState<string | null>(null);
  // Refs vivos entre renders: los lotes y los callbacks/selección de clic (leídos por el handler
  // estable montado una sola vez, para no re-montar el visor al cambiar la selección).
  const lotesRef = useRef<LoteVisor[]>([]);
  const threeRef = useRef<any>(null);
  const escenaRef = useRef<{ pedirRender: () => void; materiales: any[] } | null>(null);
  const onPickRef = useRef<Props['onPickCapa']>(onPickCapa);
  onPickRef.current = onPickCapa;
  const capasSelRef = useRef<Props['capasSel']>(capasSel);
  capasSelRef.current = capasSel;
  const onToggleInstanciaRef = useRef<Props['onToggleInstancia']>(onToggleInstancia);
  onToggleInstanciaRef.current = onToggleInstancia;
  const onPickVacioRef = useRef<Props['onPickVacio']>(onPickVacio);
  onPickVacioRef.current = onPickVacio;
  const onOcultarPiezaRef = useRef<Props['onOcultarPieza']>(onOcultarPieza);
  onOcultarPiezaRef.current = onOcultarPieza;
  const onNivelesRef = useRef<Props['onNiveles']>(onNiveles);
  onNivelesRef.current = onNiveles;
  const onPickPiezaRef = useRef<Props['onPickPieza']>(onPickPieza);
  onPickPiezaRef.current = onPickPieza;
  const onInventarioRef = useRef<Props['onInventario']>(onInventario);
  onInventarioRef.current = onInventario;
  // Datos del modelo para "Ver por nivel" / "Ocultar techo" / encuadre (se llenan al terminar de medir).
  const datosVistaRef = useRef<{
    nivelDe: Map<number, number>;
    techo: Set<number>;
    niveles: NivelIfc[];
    /** Etiquetas (tipos) que SE CONSTRUYEN por defecto: sobre ellas va el encuadre. */
    construye: Set<string>;
  } | null>(null);
  // Plano de corte horizontal (uno solo, compartido por todos los materiales) y su altura vigente, que
  // también usa el raycast (three.js no respeta el recorte al calcular impactos).
  const planoCorteRef = useRef<any>(null);
  const corteYRef = useRef<number | null>(null);
  const camaraRef = useRef<{ camera: any; controls: any } | null>(null);
  const nivelEncuadradoRef = useRef<number | null | undefined>(undefined);
  // Escena y lienzo, para el croquis de los números generadores.
  const escena3dRef = useRef<{ scene: any; renderer: any } | null>(null);
  // Mientras se toma un croquis: qué se resalta (lo demás va atenuado). null = vista normal.
  const croquisRef = useRef<((eid: number, etiqueta: string | null) => boolean) | null>(null);
  // Contorno (LineSegments) de la pieza enfocada, para que se distinga aunque esté entre otras.
  const outlineRef = useRef<any[]>([]);
  // Últimos valores de las props de vista, para `aplicarResaltado` llamada desde la carga.
  const vistaPropsRef = useRef({ capasSel, instExcluidas, instAsignadas, instOcultas, instEnfoque, instSoloVisibles, nivelVista, ocultarTecho, instAPresupuestar, instColores, avance4d, avance4dResaltadas });
  vistaPropsRef.current = { capasSel, instExcluidas, instAsignadas, instOcultas, instEnfoque, instSoloVisibles, nivelVista, ocultarTecho, instAPresupuestar, instColores, avance4d, avance4dResaltadas };

  // Carga + render + inventario. Solo depende del archivo (no de la selección).
  useEffect(() => {
    let vivo = true;
    let limpiar: (() => void) | null = null;

    (async () => {
      const host = hostRef.current;
      if (!host) return;
      const t0 = performance.now();
      try {
        // Carga perezosa por CDN (no van al bundle). vite-ignore: URLs absolutas, no las resuelve Vite.
        const THREE = await import(/* @vite-ignore */ THREE_URL);
        const { OrbitControls } = await import(/* @vite-ignore */ ORBIT_URL);
        if (!vivo) return;
        threeRef.current = THREE;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x0d1117);
        const ancho = host.clientWidth || 800;
        const alto = host.clientHeight || 600;
        const camera = new THREE.PerspectiveCamera(60, ancho / alto, 0.1, 5000);
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        // El canvas ocupa el 100 % de su contenedor por CSS y setSize NO le escribe ancho en px
        // (`false`): con ancho fijo, el canvas empujaba al contenedor, el ResizeObserver volvía a
        // disparar y la pestaña se congelaba al redimensionar los paneles.
        renderer.setSize(ancho, alto, false);
        renderer.domElement.style.width = '100%';
        renderer.domElement.style.height = '100%';
        renderer.domElement.style.display = 'block';
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.localClippingEnabled = true; // plano de corte por material ("Ver por nivel")
        planoCorteRef.current = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0); // deja lo que queda DEBAJO
        host.appendChild(renderer.domElement);
        // Base plana (Lambert + luz suave y pareja). La legibilidad la dan el color por elemento y las
        // aristas de cada pieza; sin post-proceso (ver nota junto a ORBIT_URL).
        scene.add(new THREE.HemisphereLight(0xffffff, 0x556070, 1.15));
        const dir = new THREE.DirectionalLight(0xffffff, 1.15);
        dir.position.set(30, 50, 25);
        scene.add(dir);
        const root = new THREE.Group();
        scene.add(root);

        // Materiales COMPARTIDOS por todos los lotes: el color va por vértice (RGBA), así una pieza
        // se pinta escribiendo su rango, sin un material por pieza.
        const matSolido = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide });
        const matTransp = new THREE.MeshLambertMaterial({
          vertexColors: true,
          transparent: true,
          depthWrite: false,
          side: THREE.DoubleSide,
        });
        const matArista = new THREE.LineBasicMaterial({ color: COLOR_ARISTA });
        prepararCorteDeAvance(matSolido, 'construido', avanceActivoRef.current);
        prepararCorteDeAvance(matTransp, 'falta', avanceActivoRef.current);

        const controls = new OrbitControls(camera, renderer.domElement);
        // Mouse como AutoCAD: izquierdo = girar / seleccionar (clic), MEDIO = desplazar, rueda = zoom. El
        // DERECHO queda libre para "Ocultar esta pieza" (clic sin arrastrar); OrbitControls lo usaba para
        // desplazar.
        controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: null };
        camaraRef.current = { camera, controls };

        // Dibujo A DEMANDA: un cuadro cuando algo cambió (cámara, selección, lote nuevo), no en continuo.
        let pendiente = 0;
        const pedirRender = () => {
          if (pendiente || !vivo) return;
          pendiente = requestAnimationFrame(() => {
            pendiente = 0;
            renderer.render(scene, camera);
          });
        };
        escenaRef.current = { pedirRender, materiales: [matSolido, matTransp, matArista] };
        escena3dRef.current = { scene, renderer };
        // Solo en desarrollo: para medir y depurar el visor desde la consola (nunca en el build).
        if (import.meta.env.DEV) (window as any).__visorIfc = { scene, camera, renderer, controls, lotes: lotesRef };
        controls.addEventListener('change', pedirRender);
        // Aristas apagadas mientras se mueve la cámara (son la mitad del costo del cuadro); vuelven al soltar.
        const aristasVisibles = (v: boolean) => {
          for (const l of lotesRef.current) l.lineas.visible = v;
        };
        controls.addEventListener('start', () => aristasVisibles(false));
        controls.addEventListener('end', () => {
          aristasVisibles(true);
          pedirRender();
        });

        const onResize = () => {
          const w = host.clientWidth || 800;
          const h = host.clientHeight || 600;
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h, false);
          pedirRender();
        };
        const ro = new ResizeObserver(onResize);
        ro.observe(host);

        // --- Clic: raycast contra las mallas de los lotes → vértice → trozo → pieza ---------------------
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        let downXY: { x: number; y: number; boton: number } | null = null;
        const onDown = (e: MouseEvent) => {
          downXY = { x: e.clientX, y: e.clientY, boton: e.button };
        };
        /** Impactos ordenados por distancia, ya traducidos a pieza y tipo (lo que queda sobre el corte, no). */
        const impactos = (): { eid: number; etiqueta: string | null }[] => {
          const objs: any[] = [];
          for (const l of lotesRef.current) objs.push(l.meshS, l.meshT);
          const c = corteYRef.current;
          const out: { eid: number; etiqueta: string | null }[] = [];
          for (const h of raycaster.intersectObjects(objs, false)) {
            if (c != null && h.point.y > c + 1e-4) continue;
            const l: LoteVisor | undefined = h.object.userData.lote;
            if (!l || !h.face) continue;
            const t = trozoDeVertice(l.lote.trozos, h.face.a);
            out.push({ eid: l.lote.trozos[t * T_PASO + T_EID]!, etiqueta: l.etiquetas[t] ?? null });
          }
          return out;
        };
        const onMenu = (e: MouseEvent) => e.preventDefault(); // el clic derecho es "ocultar esta pieza"
        const onUp = (e: MouseEvent) => {
          if (!downXY) return;
          const movido = Math.hypot(e.clientX - downXY.x, e.clientY - downXY.y);
          const boton = downXY.boton;
          downXY = null;
          if (movido > 5) return; // fue girar/desplazar, no un clic
          if (boton !== 0 && boton !== 2) return; // el botón medio solo desplaza
          const rect = renderer.domElement.getBoundingClientRect();
          mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
          mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
          raycaster.setFromCamera(mouse, camera);
          const hits = impactos();

          // Clic DERECHO: ocultar la pieza que está adelante (solo vista; el padre la acumula).
          if (boton === 2) {
            const eid = hits[0]?.eid;
            if (eid != null && onOcultarPiezaRef.current) onOcultarPiezaRef.current(eid);
            return;
          }

          const sel = capasSelRef.current;
          const hayTipoActivo = sel != null && sel.size > 0;
          // Con un tipo activo, el rayo busca primero piezas de ESE tipo (atraviesa los fantasmas de
          // los demás tipos, que casi no se ven); si no, lo primero visible.
          const delTipo = hayTipoActivo ? hits.find((h) => h.etiqueta != null && sel!.has(h.etiqueta)) : undefined;

          // Clic = LA PIEZA (visor BIM).
          if (onPickPiezaRef.current) {
            const hit = delTipo ?? hits[0];
            onPickPiezaRef.current(hit?.eid ?? null, e.ctrlKey || e.metaKey);
            return;
          }
          // SIN tipo activo: el clic elige un TIPO.
          if (!hayTipoActivo) {
            const et = hits.find((h) => h.etiqueta != null)?.etiqueta;
            if (et && onPickRef.current) onPickRef.current(et);
            return;
          }
          // CON tipo activo: una pieza de ese tipo se saca/reingresa aunque otra esté delante.
          if (delTipo) {
            onToggleInstanciaRef.current?.(delTipo.eid);
            return;
          }
          const etOtro = hits[0]?.etiqueta;
          if (etOtro) onPickRef.current?.(etOtro); // clic sobre otro tipo → cambiar el tipo activo
          else onPickVacioRef.current?.(); // clic en vacío → soltar la selección
        };
        renderer.domElement.addEventListener('mousedown', onDown);
        renderer.domElement.addEventListener('mouseup', onUp);
        renderer.domElement.addEventListener('contextmenu', onMenu);

        // --- El modelo lo lee el Web Worker ------------------------------------------------------------
        const parametros = await getParametrosMedicionIfc();
        if (!vivo) return;
        const worker = new Worker(new URL('../lib/ifc.worker.ts', import.meta.url), { type: 'module' });

        limpiar = () => {
          worker.terminate();
          if (pendiente) cancelAnimationFrame(pendiente);
          ro.disconnect();
          controls.dispose();
          renderer.domElement.removeEventListener('mousedown', onDown);
          renderer.domElement.removeEventListener('mouseup', onUp);
          renderer.domElement.removeEventListener('contextmenu', onMenu);
          for (const l of lotesRef.current) {
            l.geoS.dispose();
            l.geoT.dispose();
            l.geoA.dispose();
          }
          matSolido.dispose();
          matTransp.dispose();
          matArista.dispose();
          renderer.dispose();
          if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
          lotesRef.current = [];
          escenaRef.current = null;
        };

        let primerLote = true;
        worker.onmessage = (ev: MessageEvent<MensajeDelWorker>) => {
          if (!vivo) return;
          const m = ev.data;
          if (m.tipo === 'avance') {
            setEstado({ fase: 'cargando', nota: textoDeAvance(m.fase, m.fraccion, m.mb), fraccion: m.fraccion, dibujando: !primerLote });
            return;
          }
          if (m.tipo === 'error') {
            worker.terminate();
            setEstado({ fase: 'error', msg: m.msg });
            return;
          }
          if (m.tipo === 'materiales') {
            // El modelo no dice de qué son estos materiales: se reconocen por su nombre (servidor, IA
            // con caché). Solo con archivo del proyecto; sin él, quedan sin pesar como siempre.
            const contestar = (t: TiposDeMaterialAlWorker) => worker.postMessage(t);
            if (!projectId || !fileId || srcUrl) {
              contestar({ tipo: 'tiposDeMaterial', materiales: [], aviso: null });
              return;
            }
            void clasificarMaterialesIfc(projectId, fileId, m.nombres).then((r) =>
              contestar({ tipo: 'tiposDeMaterial', materiales: r.materiales, aviso: r.avisoIa }),
            );
            return;
          }
          if (m.tipo === 'lote') {
            const l = armarLoteVisor(THREE, m.lote, m.etiquetas, matSolido, matTransp, matArista);
            lotesRef.current.push(l);
            root.add(l.meshS, l.meshT, l.lineas);
            aplicarCorte();
            if (primerLote) {
              // Encuadre provisorio con lo primero que llega, para que se vea el modelo armándose.
              primerLote = false;
              const box = cajaDeTrozos(THREE, () => true);
              if (!box.isEmpty()) ajustarCamara(THREE, camera, controls, box, [0.9, 0.7, 0.9], 1.08);
            }
            pedirRender();
            return;
          }
          // fin
          worker.terminate();
          const { capas, niveles, nivelDeElemento, obras, obraDeElemento, techo, ifc, triangulos } = m;
          const construye = new Set(
            capas.filter((c) => decisionIfc(c.categoria, c.ifc?.supertipos) === 'cotiza').map((c) => c.capa),
          );
          datosVistaRef.current = { nivelDe: nivelDeElemento, techo, niveles, construye };
          onCajasDePiezasRef.current?.(cajasDePiezas());
          setAviso(ifc.aviso ?? null);

          // Encuadre y centro de giro sobre lo que SE CONSTRUYE (sin genéricos, mobiliario ni entorno).
          const box = cajaDeLoConstruido(THREE);
          if (!box.isEmpty()) ajustarCamara(THREE, camera, controls, box, [0.9, 0.7, 0.9], 1.08);

          onInventarioRef.current?.({
            bloques: [],
            capas,
            total_inserts: 0,
            tipos_de_bloque: 0,
            unidades: { codigo: 0, nombre: 'm', factor_a_metro: 1 },
            ifc,
          });
          const conPiezas = new Set(obraDeElemento.values());
          onNivelesRef.current?.(niveles.filter((n) => n.elementos > 0), nivelDeElemento, techo.size > 0, {
            obras: obras.filter((o) => conPiezas.has(o.id)),
            obraDeElemento,
          });
          aplicarCorte();
          aplicarResaltado();
          if (vistaPropsRef.current.nivelVista != null) reencuadrar();
          nivelEncuadradoRef.current = vistaPropsRef.current.nivelVista;

          // Miniatura de la primera vista: se captura JUSTO después de un render (sin
          // preserveDrawingBuffer el lienzo WebGL solo es legible en la misma tarea del render).
          renderer.render(scene, camera);
          if (onPrimeraVistaRef.current) {
            try {
              const src = renderer.domElement as HTMLCanvasElement;
              const proporcion = 16 / 10;
              let sw = src.width;
              let sh = src.height;
              if (sw / sh > proporcion) sw = sh * proporcion;
              else sh = sw / proporcion;
              const anchoMini = Math.min(480, Math.round(sw));
              const mini = document.createElement('canvas');
              mini.width = anchoMini;
              mini.height = Math.round(anchoMini / proporcion);
              const ctx2 = mini.getContext('2d');
              if (ctx2) {
                ctx2.drawImage(src, (src.width - sw) / 2, (src.height - sh) / 2, sw, sh, 0, 0, mini.width, mini.height);
                mini.toBlob((b) => { if (b && vivo) onPrimeraVistaRef.current?.(b); }, 'image/webp', 0.82);
              }
            } catch {
              /* sin miniatura: la galería muestra el ícono */
            }
          }

          setEstado({
            fase: 'listo',
            // ELEMENTOS del inventario, no mallas (una ventana son 3–4 mallas: marco, vidrios): el mismo
            // número que la ficha del plano, contado por la misma función.
            elementos: elementosDelInventarioIfc(capas),
            triangulos,
            segundos: (performance.now() - t0) / 1000,
          });
        };
        worker.onerror = (e) => {
          if (vivo) setEstado({ fase: 'error', msg: e.message || 'No se pudo leer el modelo.' });
        };
        const pedido: PedidoAlWorker = {
          tipo: 'abrir',
          url: new URL(downloadUrl, window.location.href).href,
          webIfcUrl: WEBIFC_URL,
          wasmPath: WEBIFC_WASM,
          parametros,
          anguloArista: ANGULO_ARISTA,
          limiteVertices: LOTE_VERTICES,
        };
        worker.postMessage(pedido);
      } catch (e) {
        if (!vivo) return;
        setEstado({ fase: 'error', msg: e instanceof Error ? e.message : String(e) });
      }
    })();

    return () => {
      vivo = false;
      if (limpiar) limpiar();
      if (hostRef.current) hostRef.current.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadUrl]);

  // --- Ayudas de escena (usan refs; se llaman desde la carga y desde los efectos) ----------------------

  /** La caja (mundo, m) de cada pieza, unida entre sus trozos (marco + vidrio). Se rehace solo si llegaron lotes nuevos. */
  function cajasDePiezas(): Map<number, CajaDePieza> {
    let total = 0;
    for (const l of lotesRef.current) total += l.lote.trozos.length / T_PASO;
    if (cajasPiezaRef.current.trozos === total) return cajasPiezaRef.current.mapa;
    const mapa = new Map<number, CajaDePieza>();
    for (const l of lotesRef.current) {
      const n = l.lote.trozos.length / T_PASO;
      const c = l.lote.cajas;
      for (let t = 0; t < n; t++) {
        const k = t * 6;
        if (!Number.isFinite(c[k]!) || !Number.isFinite(c[k + 3]!)) continue;
        const eid = l.lote.trozos[t * T_PASO + T_EID]!;
        mapa.set(eid, unirCajas(mapa.get(eid), [c[k]!, c[k + 1]!, c[k + 2]!, c[k + 3]!, c[k + 4]!, c[k + 5]!]));
      }
    }
    cajasPiezaRef.current = { trozos: total, mapa };
    return mapa;
  }

  /** Caja (mundo) de los trozos que pasan el filtro `(lote, trozo) → sí/no`. */
  function cajaDeTrozos(THREE: any, pasa: (l: LoteVisor, t: number) => boolean): any {
    const box = new THREE.Box3();
    const v = new THREE.Vector3();
    for (const l of lotesRef.current) {
      const n = l.lote.trozos.length / T_PASO;
      for (let t = 0; t < n; t++) {
        if (!pasa(l, t)) continue;
        const c = l.lote.cajas;
        if (!Number.isFinite(c[t * 6]!) || !Number.isFinite(c[t * 6 + 3]!)) continue;
        box.expandByPoint(v.set(c[t * 6]!, c[t * 6 + 1]!, c[t * 6 + 2]!));
        box.expandByPoint(v.set(c[t * 6 + 3]!, c[t * 6 + 4]!, c[t * 6 + 5]!));
      }
    }
    return box;
  }

  /** Caja de lo que se ve y SE CONSTRUYE; si no hay nada así, de todo lo que se ve. */
  function cajaDeLoConstruido(THREE: any): any {
    const d = datosVistaRef.current;
    const visible = (l: LoteVisor, t: number) => visibilidadDeTrozo(l, t) !== 0;
    const box = cajaDeTrozos(THREE, (l, t) => {
      const et = l.etiquetas[t];
      return et != null && (d?.construye.has(et) ?? true) && visible(l, t);
    });
    return box.isEmpty() ? cajaDeTrozos(THREE, visible) : box;
  }

  /** Cámara, near/far y CENTRO DE GIRO (target) sobre una caja. */
  function ajustarCamara(THREE: any, camera: any, controls: any, box: any, desde: [number, number, number], margen: number) {
    const { ctr, radio } = encuadrar(THREE, camera, box, desde, margen);
    camera.near = Math.max(radio / 500, 0.005);
    camera.far = Math.max(radio * 40, 200);
    camera.updateProjectionMatrix();
    controls.target.copy(ctr);
    controls.update();
    escenaRef.current?.pedirRender();
  }

  // "Ver por nivel" / "Ocultar techo": qué elementos entran en la vista (se combina con `instOcultas`).
  function pasaVista(eid: number): boolean {
    const d = datosVistaRef.current;
    if (!d) return true;
    const v = vistaPropsRef.current;
    if (v.ocultarTecho && d.techo.has(eid)) return false;
    if (v.nivelVista != null && d.nivelDe.get(eid) !== v.nivelVista) return false;
    return true;
  }

  /** 0 = oculto, 1 = sólido, 2 = translúcido, según la vista actual (sin mirar la selección). */
  function visibilidadDeTrozo(l: LoteVisor, t: number): Visibilidad {
    const v = vistaPropsRef.current;
    const eid = l.lote.trozos[t * T_PASO + T_EID]!;
    if (v.instOcultas?.has(eid)) return 0;
    if (v.instSoloVisibles != null && !v.instSoloVisibles.has(eid)) return 0;
    if (!pasaVista(eid)) return 0;
    return l.lote.colores[t * 4 + 3]! < 1 ? 2 : 1;
  }

  // Resaltado + visibilidad: al cambiar la selección/estado, atenúa lo no seleccionado, realza lo
  // pendiente y esconde lo que ordenen los comandos de visibilidad. Con un tipo activo:
  //  (1) pieza ENFOCADA desde el panel → acento magenta fuerte (para ubicarla en la escena);
  //  (2) pieza INCLUIDA del tipo activo y AÚN sin concepto → sólida + acento azul (lo que falta);
  //  (3) pieza INCLUIDA pero YA asignada a un concepto → grisada (hecha, contexto);
  //  (4) pieza EXCLUIDA (el usuario la sacó del conteo) → apagada;
  //  (5) otro tipo → fantasma (contexto, no protagoniza ni bloquea el clic).
  // Se escribe el color de cada pieza en su rango del lote y se arma el índice de lo que se ve: una
  // pieza oculta no está en el índice (ni se dibuja ni la toca el rayo).
  function aplicarResaltado() {
    const THREE = threeRef.current;
    const lotes = lotesRef.current;
    if (!THREE || lotes.length === 0) return;
    const v = vistaPropsRef.current;
    const sel = v.capasSel ?? new Set<string>();
    const excl = v.instExcluidas ?? new Set<number>();
    const asig = v.instAsignadas ?? new Set<number>();
    const enfoque = v.instEnfoque ?? null;
    const hayseleccion = sel.size > 0;
    const foco = new THREE.Color(COLOR_FOCO);
    const pendienteC = new THREE.Color(COLOR_PENDIENTE);
    const faltaC = new THREE.Color(COLOR_FALTA);
    const aPresupuestar = v.instAPresupuestar ?? null;
    const colMap = v.instColores ?? null;
    const avance = v.avance4d ?? null;
    const tonos = colMap || avance ? tonosDeEstado(THREE) : null;
    // El recorte por pieza solo existe mientras hay avance (y no un croquis): apagado, los materiales son los de siempre.
    const conAvance = avance != null && !croquisRef.current;
    avanceActivoRef.current.value = conAvance ? 1 : 0;
    const cajas = conAvance ? cajasDePiezas() : null;
    for (const ls of outlineRef.current) {
      ls.parent?.remove(ls);
      ls.geometry?.dispose?.();
      ls.material?.dispose?.();
    }
    outlineRef.current = [];

    const cro = croquisRef.current;
    for (const l of lotes) {
      const { trozos, colores } = l.lote;
      const col = l.color.array as Float32Array;
      const nT = trozos.length / T_PASO;
      const vis = new Uint8Array(nT);
      if (conAvance && !l.corte) {
        // Atributo de recorte por vértice, creado la primera vez que se pide avance (16 bytes por vértice).
        const nV = l.lote.pos.length / 3;
        const arr = new Float32Array(nV * 4);
        for (let k = 3; k < arr.length; k += 4) arr[k] = 1e9;
        l.corte = new THREE.BufferAttribute(arr, 4);
        l.geoS.setAttribute('aCorte', l.corte);
        l.geoT.setAttribute('aCorte', l.corte);
      }
      const corteArr: Float32Array | null = conAvance && l.corte ? (l.corte.array as Float32Array) : null;
      for (let t = 0; t < nT; t++) {
        let enProceso = false;
        if (cro) {
          // Croquis: lo del renglón sólido en el acento; todo lo demás, atenuado (lo oculto también se ve).
          const eid = trozos[t * T_PASO + T_EID]!;
          const si = cro(eid, l.etiquetas[t] ?? null);
          const [r, g, b, a] = si ? [pendienteC.r, pendienteC.g, pendienteC.b, 1] : [0.62, 0.65, 0.69, 0.12];
          vis[t] = a < 1 ? 2 : 1;
          const v0 = trozos[t * T_PASO + T_V0]!, vn = trozos[t * T_PASO + T_VN]!;
          for (let k = v0 * 4, fin = (v0 + vn) * 4; k < fin; k += 4) {
            col[k] = r; col[k + 1] = g; col[k + 2] = b; col[k + 3] = a;
          }
          continue;
        }
        const base = visibilidadDeTrozo(l, t);
        if (base === 0) continue;
        const eid = trozos[t * T_PASO + T_EID]!;
        const et = l.etiquetas[t];
        const activa = et != null && sel.has(et);
        let r = colores[t * 4]!, g = colores[t * 4 + 1]!, b = colores[t * 4 + 2]!, a = colores[t * 4 + 3]!;
        if (enfoque != null && eid === enfoque) {
          r = foco.r; g = foco.g; b = foco.b; a = 1;
          outlineRef.current.push(contornoDe(THREE, l, t));
        } else if (!hayseleccion && avance && tonos) {
          const f = avance.get(eid);
          if (f === undefined) a = 0.1; // sin dato de avance: contexto tenue
          else if (f >= 1 - 1e-9) { /* terminada: con los colores del modelo */ }
          else if (f <= 1e-9 || !cajas?.get(eid)) a = 0.07; // por construir (o sin caja: no se puede cortar, no se da por hecha): fantasma
          else {
            // Se está construyendo: lo hecho sólido en tono de proceso y lo que falta como fantasma (dos mallas, un corte).
            const c = tonos.accent; // no ámbar: se confunde con los techos naranjas de muchos modelos
            r = c.r; g = c.g; b = c.b; a = 1;
            enProceso = true;
          }
          // Un concepto elegido en el panel: sus piezas en el color de foco (las que aún faltan, a media opacidad; la que va a
          // medias conserva su corte) y todo lo demás atenuado, para ubicarlas en el modelo.
          const resaltadas = v.avance4dResaltadas ?? null;
          if (resaltadas && resaltadas.size > 0) {
            if (resaltadas.has(eid)) {
              // Lo construido, sólido en el color de foco; lo que falta, un fantasma tenue (se distingue de lo hecho al reproducir).
              r = foco.r; g = foco.g; b = foco.b;
              a = f !== undefined && f > 1e-9 ? 1 : 0.2;
            } else {
              // El resto de la casa NO desaparece: lo terminado queda translúcido (sigue viéndose cómo crece) para poder ver las piezas
              // marcadas por dentro; lo que va a medias, más tenue y entero; lo que aún falta, como siempre.
              if (f !== undefined && f > 1e-9) a = Math.min(a, f >= 1 - 1e-9 ? 0.3 : 0.2);
              enProceso = false;
            }
          }
        } else if (!hayseleccion && colMap && tonos) {
          const k = colMap.get(eid);
          if (k) { const c = tonos[k]; r = c.r; g = c.g; b = c.b; a = 1; } // la pieza que cambió: sólida con su tono
          else a = 0.12; // lo que no cambió: contexto
        } else if (!hayseleccion && aPresupuestar) {
          if (!aPresupuestar.has(eid)) a = 0.08; // no se presupuesta: contexto
          else if (asig.has(eid)) { r = g = b = 0.55; a = 0.3; } // ya está en la hoja
          else { r = faltaC.r; g = faltaC.g; b = faltaC.b; a = 1; } // falta
        } else if (!hayseleccion) {
          /* color del modelo */
        } else if (activa && excl.has(eid)) {
          a = 0.2; // sacada de la selección: se ve pero apagada
        } else if (activa && asig.has(eid)) {
          r = g = b = 0.5; a = 0.4; // ya tiene concepto: grisada (hecha)
        } else if (activa) {
          r = pendienteC.r; g = pendienteC.g; b = pendienteC.b; a = 1; // lo que falta
        } else {
          a = 0.1; // otro tipo: fantasma
        }
        vis[t] = enProceso ? 3 : a < 1 ? 2 : 1;
        const v0 = trozos[t * T_PASO + T_V0]!, vn = trozos[t * T_PASO + T_VN]!;
        for (let k = v0 * 4, fin = (v0 + vn) * 4; k < fin; k += 4) {
          col[k] = r; col[k + 1] = g; col[k + 2] = b; col[k + 3] = a;
        }
        if (corteArr) {
          // Plano de crecimiento de la pieza que se está construyendo; el resto, sin recorte (corte enorme).
          const f = enProceso ? avance!.get(eid)! : 0;
          const caja = enProceso ? cajas!.get(eid) : undefined;
          const cp = caja ? corteDePieza(caja, f) : null;
          for (let k = v0 * 4, fin = (v0 + vn) * 4; k < fin; k += 4) {
            if (cp) { corteArr[k] = cp.eje === 0 ? 1 : 0; corteArr[k + 1] = cp.eje === 1 ? 1 : 0; corteArr[k + 2] = cp.eje === 2 ? 1 : 0; corteArr[k + 3] = cp.corte; }
            else corteArr[k + 3] = 1e9;
          }
        }
      }
      l.color.needsUpdate = true;
      if (l.corte) l.corte.needsUpdate = true;
      const ix = indicesVisibles(l.lote, (t) => vis[t] as Visibilidad);
      l.geoS.setIndex(new THREE.BufferAttribute(ix.solido, 1));
      l.geoT.setIndex(new THREE.BufferAttribute(ix.translucido, 1));
      l.geoA.setIndex(new THREE.BufferAttribute(ix.aristas, 1));
    }
    const root = lotes[0]!.meshS.parent;
    for (const o of outlineRef.current) root?.add(o);
    aplicarCortePlanos();
    escenaRef.current?.pedirRender();
  }

  /** Contorno de la pieza enfocada (todas sus caras, sobre todo lo demás). */
  function contornoDe(THREE: any, l: LoteVisor, t: number): any {
    const o = t * T_PASO;
    const i0 = l.lote.trozos[o + T_I0]!, iN = l.lote.trozos[o + T_IN]!;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(l.lote.pos, 3));
    g.setIndex(new THREE.BufferAttribute(l.lote.idx.slice(i0, i0 + iN), 1));
    const eg = new THREE.EdgesGeometry(g);
    g.dispose();
    const lm = new THREE.LineBasicMaterial({ color: COLOR_FOCO, depthTest: false });
    const ls = new THREE.LineSegments(eg, lm);
    ls.renderOrder = 999;
    return ls;
  }

  useEffect(() => {
    aplicarResaltado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capasSel, instExcluidas, instAsignadas, instEnfoque, instOcultas, instSoloVisibles, instAPresupuestar, instColores, avance4d, avance4dResaltadas]);

  /**
   * El croquis de unas piezas: se pintan (lo pedido en el acento, lo demás atenuado, sin corte ni piezas
   * ocultas), se encuadran desde arriba en diagonal, se dibuja y se lee el lienzo en la MISMA tarea (sin
   * preserveDrawingBuffer el lienzo WebGL solo es legible justo después del render). Después todo vuelve.
   */
  async function capturarCroquis(pedido: PedidoCroquisIfc): Promise<Croquis | null> {
    const THREE = threeRef.current;
    const cam = camaraRef.current;
    const e3 = escena3dRef.current;
    if (!THREE || !cam || !e3 || !datosVistaRef.current || lotesRef.current.length === 0) return null;
    const piezas = new Set(pedido.piezas ?? []);
    const etiquetas = new Set(pedido.etiquetas ?? []);
    if (piezas.size === 0 && etiquetas.size === 0) return null;
    const resalta = (eid: number, et: string | null) => piezas.has(eid) || (et != null && etiquetas.has(et));
    const box = cajaDeTrozos(THREE, (l, t) => resalta(l.lote.trozos[t * T_PASO + T_EID]!, l.etiquetas[t] ?? null));
    if (box.isEmpty()) return null;
    const pos = cam.camera.position.clone();
    const target = cam.controls.target.clone();
    const near = cam.camera.near;
    const far = cam.camera.far;
    const aspecto = cam.camera.aspect;
    const fondo = e3.scene.background;
    const corte = corteYRef.current;
    const lienzo = e3.renderer.domElement as HTMLCanvasElement;
    const tam = { w: lienzo.clientWidth || CROQUIS_ANCHO, h: lienzo.clientHeight || CROQUIS_ALTO };
    let out: Croquis | null = null;
    try {
      croquisRef.current = resalta;
      corteYRef.current = null;
      aplicarResaltado();
      // Siempre del mismo tamaño (no el de la pantalla): la hoja impresa sale igual en cualquier monitor.
      e3.renderer.setPixelRatio(1);
      e3.renderer.setSize(CROQUIS_ANCHO, CROQUIS_ALTO, false);
      cam.camera.aspect = CROQUIS_ANCHO / CROQUIS_ALTO;
      cam.camera.updateProjectionMatrix();
      ajustarCamara(THREE, cam.camera, cam.controls, box, [0.9, 0.75, 0.9], 1.15);
      e3.scene.background = new THREE.Color(0xffffff);
      e3.renderer.render(e3.scene, cam.camera);
      const url = reducirLienzo(lienzo, CROQUIS_ANCHO);
      // Dónde cae cada pieza rotulada: el centro de su caja, proyectado con la cámara del croquis.
      const marcas: Croquis['marcas'] = [];
      for (const mk of pedido.marcas ?? []) {
        const ps = new Set(mk.piezas ?? []);
        const es = new Set(mk.etiquetas ?? []);
        const b = cajaDeTrozos(THREE, (l, t) => {
          const eid = l.lote.trozos[t * T_PASO + T_EID]!;
          const et = l.etiquetas[t] ?? null;
          return ps.has(eid) || (et != null && es.has(et));
        });
        if (b.isEmpty()) continue;
        const p = b.getCenter(new THREE.Vector3()).project(cam.camera);
        const x = (p.x + 1) / 2;
        const y = (1 - p.y) / 2;
        if (x >= 0 && x <= 1 && y >= 0 && y <= 1) marcas.push({ k: mk.k, x, y });
      }
      out = { url, marcas };
    } catch {
      out = null;
    } finally {
      croquisRef.current = null;
      corteYRef.current = corte;
      e3.scene.background = fondo;
      e3.renderer.setPixelRatio(window.devicePixelRatio);
      e3.renderer.setSize(tam.w, tam.h, false);
      cam.camera.aspect = aspecto;
      cam.camera.position.copy(pos);
      cam.camera.near = near;
      cam.camera.far = far;
      cam.camera.updateProjectionMatrix();
      cam.controls.target.copy(target);
      cam.controls.update();
      aplicarResaltado();
    }
    return out;
  }
  const capturarRef = useRef(capturarCroquis);
  capturarRef.current = capturarCroquis;
  useEffect(() => {
    if (!onCapturador) return;
    onCapturador(estado.fase === 'listo' ? (p) => capturarRef.current(p) : null);
  }, [onCapturador, estado.fase]);
  useEffect(() => () => onCapturador?.(null), [onCapturador]);

  // Órdenes de cámara (botones / atajos del padre): acercar a lo elegido, vista superior, todo.
  useEffect(() => {
    if (!ordenCamara || !datosVistaRef.current) return;
    const THREE = threeRef.current;
    const cam = camaraRef.current;
    if (!THREE || !cam) return;
    const sel = capasSel ?? new Set<string>();
    const excl = instExcluidas ?? new Set<number>();
    let box: any;
    if (ordenCamara.vista === 'seleccion') {
      box = cajaDeTrozos(THREE, (l, t) => {
        if (visibilidadDeTrozo(l, t) === 0) return false;
        const eid = l.lote.trozos[t * T_PASO + T_EID]!;
        const et = l.etiquetas[t];
        return instEnfoque != null ? eid === instEnfoque : et != null && sel.has(et) && !excl.has(eid);
      });
    } else if (ordenCamara.vista === 'resaltadas') {
      // «Revisar el modelo» → «Ver en el modelo»: acerca a las piezas señaladas.
      box = cajaDeTrozos(THREE, (l, t) => visibilidadDeTrozo(l, t) !== 0 && (instColores?.has(l.lote.trozos[t * T_PASO + T_EID]!) ?? false));
    } else box = cajaDeLoConstruido(THREE);
    if (box.isEmpty()) return;
    const c = corteYRef.current;
    if (c != null && box.max.y > c) box.max.y = Math.max(box.min.y, c);
    let desde: [number, number, number] = [0.9, 0.7, 0.9];
    if (ordenCamara.vista === 'arriba') desde = [0, 1, 0.02];
    else if (ordenCamara.vista === 'seleccion' || ordenCamara.vista === 'resaltadas') {
      const d = cam.camera.position.clone().sub(cam.controls.target); // conserva el ángulo actual
      if (d.lengthSq() > 0) desde = [d.x, d.y, d.z];
    }
    ajustarCamara(THREE, cam.camera, cam.controls, box, desde, ordenCamara.vista === 'seleccion' || ordenCamara.vista === 'resaltadas' ? 1.4 : 1.08);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenCamara?.n]);

  /** Dónde cae en pantalla lo seleccionado (o lo que se construye): proyecta las 8 esquinas de su caja. */
  function cajaEnPantalla(todo?: boolean): CajaEnPantalla | null {
    const THREE = threeRef.current;
    const cam = camaraRef.current;
    const host = hostRef.current;
    if (!THREE || !cam || !host || !datosVistaRef.current) return null;
    try {
      const v = vistaPropsRef.current;
      const sel = todo ? new Set<string>() : v.capasSel ?? new Set<string>();
      const excl = v.instExcluidas ?? new Set<number>();
      let box = sel.size > 0
        ? cajaDeTrozos(THREE, (l, t) => {
            if (visibilidadDeTrozo(l, t) === 0) return false;
            const et = l.etiquetas[t];
            return et != null && sel.has(et) && !excl.has(l.lote.trozos[t * T_PASO + T_EID]!);
          })
        : cajaDeLoConstruido(THREE);
      if (box.isEmpty()) box = cajaDeLoConstruido(THREE);
      if (box.isEmpty()) return null;
      const W = host.clientWidth;
      const H = host.clientHeight;
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      const p = new THREE.Vector3();
      for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
        p.set(x, y, z).project(cam.camera);
        const sx = ((p.x + 1) / 2) * W;
        const sy = ((1 - p.y) / 2) * H;
        if (sx < x0) x0 = sx;
        if (sy < y0) y0 = sy;
        if (sx > x1) x1 = sx;
        if (sy > y1) y1 = sy;
      }
      // Solo lo que cae dentro de la vista (una caja que se sale de cuadro no debe empujar la miniatura).
      x0 = Math.max(0, x0); y0 = Math.max(0, y0); x1 = Math.min(W, x1); y1 = Math.min(H, y1);
      if (!(x1 > x0 && y1 > y0)) return null;
      return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    } catch {
      return null;
    }
  }
  // Se re-entrega en cada render para que lea siempre la selección vigente.
  useEffect(() => {
    if (!onCajaEnPantalla) return;
    onCajaEnPantalla(estado.fase === 'listo' ? cajaEnPantalla : null);
  });
  useEffect(() => () => onCajaEnPantalla?.(null), [onCajaEnPantalla]);

  /** Plano de corte (o ninguno) en los materiales compartidos y en el contorno de foco. */
  function aplicarCortePlanos() {
    const plano = planoCorteRef.current;
    const planos = corteYRef.current != null && plano ? [plano] : null;
    const poner = (m: any) => {
      if (!m) return;
      const antes = m.clippingPlanes?.length ?? 0;
      m.clippingPlanes = planos;
      if (antes !== (planos?.length ?? 0)) m.needsUpdate = true; // cambia el programa del shader
    };
    for (const m of escenaRef.current?.materiales ?? []) poner(m);
    for (const ls of outlineRef.current) poner(ls.material);
  }

  // Plano de corte a `piso del nivel + alturaCorte`. Una PARTE de infraestructura (sin altura de piso)
  // no se corta: solo se filtra.
  function aplicarCorte() {
    const d = datosVistaRef.current;
    const plano = planoCorteRef.current;
    if (!plano) return;
    const nv = vistaPropsRef.current.nivelVista;
    const nivel = d && nv != null ? d.niveles.find((n) => n.id === nv) : undefined;
    // alturaCorte null = ver el nivel COMPLETO, sin cortar (estructura, instalaciones).
    const corteY = nivel?.elevacion != null && alturaCorte != null ? nivel.elevacion + alturaCorte : null;
    corteYRef.current = corteY;
    if (corteY != null) plano.constant = corteY;
    aplicarCortePlanos();
  }

  // Encuadre de lo que quedó a la vista (al cambiar de nivel): desde más arriba si es un piso cortado,
  // para que se lea como planta.
  function reencuadrar() {
    const THREE = threeRef.current;
    const cam = camaraRef.current;
    if (!THREE || !cam) return;
    const box = cajaDeLoConstruido(THREE);
    if (box.isEmpty()) return;
    const c = corteYRef.current;
    if (c != null && box.max.y > c) box.max.y = Math.max(box.min.y, c);
    const desde: [number, number, number] = c != null ? [0.55, 1.3, 0.65] : [0.9, 0.7, 0.9];
    ajustarCamara(THREE, cam.camera, cam.controls, box, desde, 1.08);
  }

  useEffect(() => {
    aplicarCorte();
    aplicarResaltado();
    if (datosVistaRef.current && nivelEncuadradoRef.current !== nivelVista) {
      nivelEncuadradoRef.current = nivelVista;
      reencuadrar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivelVista, alturaCorte, ocultarTecho]);

  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0, width: '100%' }}>
      <div ref={hostRef} style={{ position: 'absolute', inset: 0 }} />
      {estado.fase === 'cargando' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            // Mientras llegan los lotes, el modelo se ve armándose detrás del aviso.
            background: estado.dibujando ? 'rgba(13,17,23,.55)' : '#0d1117',
            color: '#c9d1d9',
            pointerEvents: estado.dibujando ? 'none' : 'auto',
          }}
        >
          <Spinner size="large" label={estado.nota} />
          {estado.fraccion != null && (
            <div style={{ width: 240, height: 4, borderRadius: 2, background: 'rgba(255,255,255,.12)', overflow: 'hidden' }}>
              <div style={{ width: `${Math.round(estado.fraccion * 100)}%`, height: '100%', background: '#2b8cff', transition: 'width 150ms' }} />
            </div>
          )}
        </div>
      )}
      {estado.fase === 'error' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
            background: '#0d1117',
            color: '#e5484d',
          }}
        >
          No se pudo mostrar el modelo IFC: {estado.msg}
        </div>
      )}
      {aviso && estado.fase === 'listo' && (
        <div
          role="status"
          style={{
            position: 'absolute',
            top: 90,
            left: 12,
            right: 12,
            maxWidth: 640,
            margin: '0 auto',
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(58,44,8,.92)',
            border: '1px solid rgba(250,204,21,.45)',
            color: '#fde68a',
            fontSize: 12.5,
            lineHeight: 1.4,
          }}
        >
          {aviso}
        </div>
      )}
      {estado.fase === 'listo' && !sinBarraDeEstado && (
        // Barra de estado FIJA al pie del visor, como la de AutoCAD: los gestos y los atajos, sin
        // encimarse con los botones de arriba.
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minHeight: 26,
            background: 'rgba(13,17,23,.92)',
            borderTop: '1px solid rgba(255,255,255,.08)',
            color: '#c9d1d9',
            padding: '3px 12px',
            fontSize: 11.5,
            lineHeight: 1.4,
            pointerEvents: 'none',
          }}
        >
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {onPickPieza
              ? 'Clic: elegir · Ctrl+clic: sumar o quitar · Clic derecho: ocultar · H ocultar · I aislar · F acercar · Esc soltar · Rueda: acercar · Botón de en medio: mover'
              : 'Arrastrar: girar · Rueda: acercar · Botón de en medio: mover'}
          </span>
          <span style={{ color: '#8b949e', flexShrink: 0 }}>
            {estado.elementos.toLocaleString('es-MX')} {estado.elementos === 1 ? 'pieza' : 'piezas'}
          </span>
        </div>
      )}
    </div>
  );
}

/** Arma las mallas de un lote: los atributos se comparten; cada malla tiene su índice. */
/**
 * Recorte por pieza para el avance de obra (idea 11). El modelo se dibuja en lotes con un material compartido: no
 * hay un plano de recorte por pieza como en el demo. Cada vértice trae `aCorte` = (eje, corte): el fragmento se
 * descarta si su posición, proyectada en el eje, pasa del corte. Lo construido se dibuja con el material sólido
 * ('construido': descarta lo de arriba del corte) y lo que falta con el translúcido ('falta': descarta lo de abajo y
 * se aclara), así una pieza a medias se ve construirse sin hueco ni traslape. Corte ≥ 1e8 = sin recorte (todo lo
 * demás). Con el uniforme apagado el material se comporta exactamente como antes.
 */
function prepararCorteDeAvance(mat: any, modo: 'construido' | 'falta', activo: { value: number }): void {
  const NL = String.fromCharCode(10);
  const unir = (...l: string[]) => l.join(NL);
  mat.onBeforeCompile = (sh: any) => {
    sh.uniforms.uAvance = activo;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', unir('#include <common>', 'attribute vec4 aCorte;', 'varying vec4 vCorte;', 'varying vec3 vPosModelo;'))
      .replace('#include <begin_vertex>', unir('#include <begin_vertex>', 'vCorte = aCorte;', 'vPosModelo = position;'));
    // Un recorte es válido solo con un eje (vector unitario) y un corte finito: si el lote NO trae el atributo, WebGL entrega
    // (0,0,0,1) y esto jamás recorta. Invariante: `position` y las cajas están en el mismo espacio (el root no se transforma).
    const hayRecorte = 'uAvance > 0.5 && dot(vCorte.xyz, vCorte.xyz) > 0.5 && vCorte.w < 1.0e8';
    const descarta =
      modo === 'construido'
        ? 'if (' + hayRecorte + ' && dot(vPosModelo, vCorte.xyz) > vCorte.w) discard;'
        : 'if (' + hayRecorte + ' && dot(vPosModelo, vCorte.xyz) < vCorte.w) discard;';
    const tenue = modo === 'falta' ? 'if (' + hayRecorte + ') diffuseColor.a *= 0.18;' : '';
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', unir('#include <common>', 'uniform float uAvance;', 'varying vec4 vCorte;', 'varying vec3 vPosModelo;'))
      .replace('#include <clipping_planes_fragment>', unir('#include <clipping_planes_fragment>', descarta))
      .replace('#include <color_fragment>', unir('#include <color_fragment>', tenue));
  };
  mat.customProgramCacheKey = () => 'avance4d-' + modo;
}

function armarLoteVisor(
  THREE: any,
  lote: Lote,
  etiquetas: (string | null)[],
  matSolido: any,
  matTransp: any,
  matArista: any,
): LoteVisor {
  const nV = lote.pos.length / 3;
  const rgba = new Float32Array(nV * 4);
  const nT = lote.trozos.length / T_PASO;
  const vis: Visibilidad[] = [];
  for (let t = 0; t < nT; t++) {
    const v0 = lote.trozos[t * T_PASO + T_V0]!, vn = lote.trozos[t * T_PASO + T_VN]!;
    const c = lote.colores.subarray(t * 4, t * 4 + 4);
    for (let k = v0 * 4, fin = (v0 + vn) * 4; k < fin; k += 4) rgba.set(c, k);
    vis.push(c[3]! < 1 ? 2 : 1);
  }
  const pos = new THREE.BufferAttribute(lote.pos, 3);
  const nor = new THREE.BufferAttribute(lote.nor, 3);
  const color = new THREE.BufferAttribute(rgba, 4);
  const ix = indicesVisibles(lote, (t) => vis[t]!);
  const geo = (indice: Uint32Array) => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', pos);
    g.setAttribute('normal', nor);
    g.setAttribute('color', color);
    g.setIndex(new THREE.BufferAttribute(indice, 1));
    return g;
  };
  const geoS = geo(ix.solido);
  const geoT = geo(ix.translucido);
  // La esfera envolvente se calcula con TODAS las posiciones (no cambia al ocultar piezas).
  geoS.computeBoundingSphere();
  geoT.boundingSphere = geoS.boundingSphere;
  geoS.computeBoundingBox();
  geoT.boundingBox = geoS.boundingBox;
  const geoA = new THREE.BufferGeometry();
  geoA.setAttribute('position', new THREE.BufferAttribute(lote.aristas, 3));
  geoA.setIndex(new THREE.BufferAttribute(ix.aristas, 1));
  geoA.boundingSphere = geoS.boundingSphere;
  const meshS = new THREE.Mesh(geoS, matSolido);
  const meshT = new THREE.Mesh(geoT, matTransp);
  meshT.renderOrder = 1; // lo translúcido después de lo sólido
  const lineas = new THREE.LineSegments(geoA, matArista);
  const l: LoteVisor = { lote, etiquetas, color, geoS, geoT, geoA, corte: null, meshS, meshT, lineas };
  meshS.userData.lote = l;
  meshT.userData.lote = l;
  return l;
}
